import { MultisetRestClient } from './multisetRestClient';
import { pgSelect, pgInsert, pgUpdateWhere } from './supabaseClient';
import { logEvent } from './debugLog';

/**
 * Venue registry maintenance shared by Setup (🔄 Refresh) and the Dashboard
 * map bar (📍 pin / 🗺 auto-locate). All writes hit venue_settings and are
 * RLS-guarded (super, or the owning customer for updates).
 */

async function upsertVenue(mapCode: string, patch: Record<string, unknown>): Promise<void> {
  const n = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    patch,
  );
  if (n === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, enabled_langs: ['en'], ...patch });
  }
}

/**
 * Fetches every map on the MultiSet account and publishes name + GPS (when
 * georeferenced; [0,0] = not georeferenced → no pin) to the portal registry.
 * Returns a human summary line.
 */
export async function refreshAccountMaps(
  clientId: string,
  clientSecret: string,
  onProgress: (msg: string) => void,
): Promise<string> {
  const rest = new MultisetRestClient({ clientId, clientSecret, mapType: 'map', code: '' });
  onProgress('Listing account maps…');
  const maps = (await rest.listMaps()).filter((m) => m.mapCode);
  if (maps.length === 0) return 'No maps with codes found on this account.';
  const existing = new Set(
    (await pgSelect<{ map_code: string }>('venue_settings', 'select=map_code').catch(() => [])).map(
      (r) => r.map_code,
    ),
  );
  let published = 0;
  let pinned = 0;
  let fresh = 0;
  const errors: string[] = [];
  for (const m of maps) {
    onProgress(`Publishing ${published + errors.length + 1}/${maps.length} — ${m.mapCode}…`);
    try {
      const d = await rest.getMapDetails(m.mapCode);
      const [lng, lat] = d.location?.coordinates ?? [];
      const hasGps = typeof lat === 'number' && typeof lng === 'number' && !(lat === 0 && lng === 0);
      await upsertVenue(m.mapCode, {
        venue_name: d.mapName,
        ...(hasGps ? { lat, lng } : {}),
      });
      published++;
      if (hasGps) pinned++;
      if (!existing.has(m.mapCode)) fresh++;
    } catch (err) {
      errors.push(`${m.mapCode}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  logEvent('admin', `map refresh: ${published} published, ${fresh} new, ${pinned} pinned`);
  return (
    `🔄 ${published}/${maps.length} map(s) synced — ${fresh} new, ${pinned} with GPS pins` +
    (published > pinned ? ` (${published - pinned} not georeferenced — no pin)` : '') +
    (errors.length ? `. Failed: ${errors[0]}` : '.')
  );
}

/** Pins a venue at the device's current GPS position. Resolves to a summary. */
export function pinVenueHere(mapCode: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!mapCode) {
      reject(new Error('No map selected.'));
      return;
    }
    if (!('geolocation' in navigator)) {
      reject(new Error('This browser has no geolocation support.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        upsertVenue(mapCode, { lat, lng })
          .then(() =>
            resolve(`📍 pinned ${mapCode} at ${lat.toFixed(6)}, ${lng.toFixed(6)} (±${Math.round(accuracy)} m)`),
          )
          .catch((err: unknown) => reject(err instanceof Error ? err : new Error(String(err))));
      },
      (err) => reject(new Error(`Location unavailable: ${err.message}`)),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  });
}

interface NominatimHit {
  lat: string;
  lon: string;
  class: string;
}

/**
 * OSM Nominatim forward geocode. Broad-area matches (place/boundary — whole
 * cities/regions) are rejected: a venue pinned at a city centroid is worse
 * than no pin.
 */
async function geocodeName(name: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(name)}`,
      { headers: { Accept: 'application/json' } },
    );
    if (!res.ok) return null;
    const hits = (await res.json()) as NominatimHit[];
    const h = hits[0];
    if (!h || h.class === 'boundary' || h.class === 'place') return null;
    const lat = Number(h.lat);
    const lng = Number(h.lon);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

/** Geocodes every un-pinned venue from its name (1.2 s etiquette pacing). */
export async function autoLocateUnpinned(onProgress: (msg: string) => void): Promise<string> {
  const rows = await pgSelect<{ map_code: string; venue_name: string | null }>(
    'venue_settings',
    'select=map_code,venue_name&lat=is.null&order=venue_name',
  );
  if (rows.length === 0) return 'Every published venue already has a pin.';
  let pinned = 0;
  const skipped: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const label = rows[i].venue_name || rows[i].map_code;
    onProgress(`Locating ${i + 1}/${rows.length} — ${label}…`);
    const hit = await geocodeName(label);
    if (hit) {
      await upsertVenue(rows[i].map_code, { lat: hit.lat, lng: hit.lng });
      pinned++;
    } else {
      skipped.push(label);
    }
    if (i < rows.length - 1) await new Promise((r) => setTimeout(r, 1200));
  }
  return (
    `🗺 pinned ${pinned}/${rows.length} venue(s) from their names` +
    (skipped.length ? `. Couldn't locate: ${skipped.join(', ')} — use 📍 on site or georeference in MultiSet.` : '.')
  );
}
